// src/services/api.js
import axios from 'axios'
import {
  getAccessToken,
  getRefreshToken,
  setAccessTokenToLocalStorage,
  setRefreshTokenToLocalStorage,
  setAccessTokenToSessionStorage,
  setRefreshTokenToSessionStorage,
  clearCredentials,
  clearCredentialsFromCookies,
  extractTokensFromResponse,
  setTokenExpiration,
  getActiveStorageType,
  getAccessTokenToCookies,
} from '@/shared/utils/tokenHelper'
import router from '@/router'
import { pushRefreshedTokenToSocket } from '@/shared/services/webSocket'
import { getAuthStoreSafe, getUserStoreSafe } from '@/shared/utils/piniaSafe.util'
import i18n from '@/i18n';
import { retryWithBackoff } from '@/shared/utils/retryWithBackoff';
import { isPublicGuestApiPath, isGuestModeEnabled, isGuestPostPath, guestDebug } from '@/shared/config/guest.config';
import { isGuestSession } from '@/shared/utils/authSession.util';
import { refreshCookies } from '@/domains/auth/services/auth.service';
import {
  isRefreshing,
  executeRefresh,
  isCookieRefreshing,
  executeCookieRefresh
} from '@/shared/services/refreshLock';

const { t } = i18n.global;

const isUseCookies = process.env.VUE_APP_USE_COOKIES === 'true';

// Helper function to check if error indicates session expired or authenticated user not found
const isSessionExpiredOrUserNotFound = (error) => {
  const errorCode = error?.response?.data?.code || '';
  const errorMessage = (error?.response?.data?.message || '').toLowerCase();
  const status = error?.response?.status;

  const sessionExpiredCodes = ['AUTH_SESSION_EXPIRED', 'AUTH_INVALID_TOKEN', 'SESSION_EXPIRED'];
  const authUserNotFoundCodes = ['AUTH_USER_NOT_FOUND'];

  if (errorMessage.includes('deactivated')) {
    return false;
  }

  if (sessionExpiredCodes.includes(errorCode) || authUserNotFoundCodes.includes(errorCode)) {
    return true;
  }

  if (errorMessage.includes('session expired') ||
    errorMessage.includes('invalid token') ||
    errorMessage.includes('token expired') ||
    errorMessage.includes('no token found')) {
    return true;
  }

  return false;
};

// Helper function to check if we're on an auth page (login, signup, etc.)
// Kept for backward compatibility, but should use shouldSuppressError from authState instead
const isAuthPage = () => {
  try {
    if (typeof window === 'undefined' || !router) return false;
    const currentRoute = router.currentRoute.value;
    return currentRoute.name === 'auth1.sign-in1' ||
      currentRoute.name === 'auth1.sign-up1' ||
      currentRoute.name === 'auth1.recoverpassword1' ||
      currentRoute.name === 'auth1.verify-account' ||
      currentRoute.name === 'auth1.delete-user' ||
      currentRoute.name === 'auth1.complete-registration';
  } catch (error) {
    // If router is not available, return false
    return false;
  }
};

import { performSessionLogout } from '@/shared/utils/sessionLogout.util';
import {
  isAllowedDuringOAuthOnboarding,
  redirectToCompleteRegistration,
} from '@/shared/utils/oauthOnboarding.util';
import { parseSafeRedirectPath } from '@/shared/utils/authRedirect.util';

function isOnGuestPublicRoute() {
  try {
    const route = router.currentRoute.value;
    if (route?.name === 'guest.post-detail'
      || route?.meta?.guestPost === true
      || route?.meta?.guestMarket === true) {
      return true;
    }
    return isGuestPostPath(route?.path || globalThis.location?.pathname || '');
  } catch {
    return isGuestPostPath(globalThis.location?.pathname || '');
  }
}

/** Anonymous / guest pages must not be redirected to auth on 401 from background API calls. */
function shouldSkipAuthRedirectFromApiError(requestUrl = '') {
  if (isPublicGuestApiPath(requestUrl)) return true;
  if (isGuestSession()) return true;
  if (isGuestModeEnabled() && isOnGuestPublicRoute()) return true;
  return false;
}

// Helper function to logout user completely
const performLogout = async (reason = 'unknown', extra = {}) => {
  const requestUrl = extra.url || '';
  if (shouldSkipAuthRedirectFromApiError(requestUrl)) {
    guestDebug('api:performLogout blocked (guest/anonymous context)', { reason, ...extra });
    return;
  }
  try {
    guestDebug('api:performLogout → auth page', {
      reason,
      route: router.currentRoute.value?.fullPath,
      routeName: router.currentRoute.value?.name,
      ...extra,
    });
    await performSessionLogout();
  } catch (error) {
    console.error('Error during logout:', error);
  }
}

// Dynamic API base URL - uses current page origin
// This allows both https://1tik.social and http://192.168.30.22 to work
const getApiBaseUrl = () => {
  // Allow override via environment variable for custom configurations
  const envBaseUrl = process.env.VUE_APP_API_BASE_URL;
  return envBaseUrl;


  if (envBaseUrl) {
    try {
      // Allow absolute URLs and path-only values (use current origin as base)
      const url = new URL(envBaseUrl, globalThis.location.origin);
      // Extract path (e.g., /api/0.0.1) and combine with current origin
      return `${globalThis.location.origin}${url.pathname}`;
    } catch (error) {
      // Failed to parse VUE_APP_API_BASE_URL
      console.debug('Failed to parse VUE_APP_API_BASE_URL:', error);
    }
  }

  // Default: use current origin + standard API path
  return `${globalThis.location.origin}/apimain`;
};

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  withCredentials: isUseCookies,
  timeout: 30000, // 30 seconds default timeout for all requests
})

// Helper function to calculate exponential backoff delay
const getExponentialBackoffDelay = (attempt, initialDelayMs = 1000, maxDelayMs = 10000, backoffMultiplier = 2) => {
  const delay = Math.min(initialDelayMs * Math.pow(backoffMultiplier, attempt), maxDelayMs);
  return Math.floor(delay);
};

// Helper function to retry request with exponential backoff
const retryRequestWithBackoff = async (requestFn, attempt = 0, maxRetries = 3) => {
  try {
    return await requestFn();
  } catch (error) {
    // Only retry on 503, 500, or network errors
    const shouldRetry = (error?.response?.status === 503 ||
      error?.response?.status === 500 ||
      !error.response) && attempt < maxRetries;

    if (shouldRetry) {
      const delay = getExponentialBackoffDelay(attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryRequestWithBackoff(requestFn, attempt + 1, maxRetries);
    }
    throw error;
  }
};

const getUiSafeErrorMessage = (status) => {
  if (status === 503) return t('general.serviceUnavailable') || 'Service temporarily unavailable';
  if (status === 500) return t('general.serverError') || 'Server error';
  if (status === 404) return t('general.notFound') || 'Resource not found';
  if (status === 403) return t('general.forbidden') || 'Access denied';
  if (status === 400) return t('general.badRequest') || 'Bad request';
  if (status === 409) return t('general.conflict') || 'Conflict detected';
  if (status === 429) return t('general.rateLimitWarning') || 'Too many requests';
  return t('general.unknownError') || 'An error occurred';
};

const applyUiSafeErrorMessage = (error) => {
  const status = Number(error?.response?.status) || 0;
  if (!status) return;
  error.response.data = error.response.data || {};
  const requestPath = String(error?.response?.data?.path || error?.config?.url || '').toLowerCase();
  const isGamificationProfileGuardError =
    status === 403 &&
    requestPath.includes('/gamification/v2/profile');
  if (isGamificationProfileGuardError) {
    return;
  }
  if (
    status === 403 &&
    error?.response?.data?.code === 'AUTH_OAUTH_ONBOARDING_REQUIRED'
  ) {
    return;
  }
  error.response.data.message = getUiSafeErrorMessage(status);
};

const handleOAuthOnboardingRequired = (error, requestUrl) => {
  if (
    error?.response?.status === 403 &&
    error?.response?.data?.code === 'AUTH_OAUTH_ONBOARDING_REQUIRED' &&
    !isAllowedDuringOAuthOnboarding(requestUrl)
  ) {
    const current = router.currentRoute?.value;
    const redirectPath =
      parseSafeRedirectPath(current?.query?.redirect) ||
      parseSafeRedirectPath(current?.fullPath && current.name !== 'auth1.complete-registration' ? current.fullPath : null);
    redirectToCompleteRegistration(router, redirectPath);
    return true;
  }
  return false;
};

if (!isUseCookies) {
  // Request interceptor - add access token to headers only
  // Removed proactive refresh to prevent infinite loops
  apiClient.interceptors.request.use(
    (config) => {
      // ⚠️ CRITICAL: Prevent API calls if user has logged out
      const authStore = getAuthStoreSafe();

      // ✅ FIX: Whitelist auth endpoints to allow login even if logged out
      const isAuthEndpoint = config.url && (
        config.url.includes('/auth/login') ||
        config.url.includes('/auth/signin') ||
        config.url.includes('/auth/sign-in') ||
        config.url.includes('/auth/sign-up') ||
        config.url.includes('/auth/cookie/login') ||
        config.url.includes('/auth/cookie/signin') ||
        config.url.includes('/auth/cookie/signup') ||
        config.url.includes('/auth/refresh-token') ||
        config.url.includes('/auth/logout') ||
        config.url.includes('/auth/oauth') ||
        config.url.includes('/auth/onboarding') ||
        config.url.includes('/auth/login/methods') ||
        config.url.includes('/auth/recover') ||
        config.url.includes('/auth/reset') ||
        config.url.includes('/auth/verify')
      );

      const requestUrl = config.url || '';
      if (isPublicGuestApiPath(requestUrl)) {
        guestDebug('api:request skip auth header (public guest path)', { url: requestUrl });
        delete config.headers.Authorization;
        return config;
      }

      if (authStore?.shouldPreventApiCall && !isAuthEndpoint) {
        console.debug('[API] Preventing API call after logout:', config.url);
        return Promise.reject(new Error('User has logged out'));
      }

      const accessToken = getAccessToken(); // Checks both localStorage and sessionStorage
      // ✅ FIX: Validate token format before adding to headers
      if (accessToken && accessToken !== 'null' && accessToken !== 'undefined' && accessToken.trim() !== '') {
        // Basic JWT validation: should have 3 parts separated by dots
        const parts = accessToken.split('.');
        if (parts.length === 3 && parts.every(part => part.length > 0)) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        } else {
          console.warn('[API] Invalid token format, skipping Authorization header');
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  )

  // Response interceptor - handle token refresh
  apiClient.interceptors.response.use(
    function onFulfilled(response) {
      return response;
    },
    async function onRejected(error) {
      const originalRequest = error.config;
      const requestUrl = originalRequest?.url || '';
      if (isPublicGuestApiPath(requestUrl)) {
        guestDebug('api:response error on public guest path (no logout)', {
          url: requestUrl,
          status: error?.response?.status,
        });
        return Promise.reject(error);
      }

      const refreshToken = getRefreshToken(); // Uses active storage type tracking

      // Determine which storage type to use based on active storage type
      const activeStorageType = getActiveStorageType();
      const isUsingSessionStorage = activeStorageType === 'sessionStorage';

      // Exclude refresh-token endpoint from retry logic to prevent loops
      const isRefreshEndpoint = originalRequest.url?.includes('/auth/refresh-token');

      const isCriticalError = isSessionExpiredOrUserNotFound(error);
      const errorMessage = (error?.response?.data?.message || '').toLowerCase();
      const isDeactivatedAccount = errorMessage.includes('deactivated');

      // Only logout immediately for critical errors when we cannot try refresh (per backend guide: on 401 try refresh first)
      const canTryRefresh = error?.response?.status === 401 && refreshToken && !isRefreshEndpoint;
      if (isCriticalError && !isDeactivatedAccount && !canTryRefresh) {
        await performLogout('jwt:critical-error-no-refresh', { url: requestUrl, status: error?.response?.status });
        throw error;
      }

      // Try to refresh token on 401 error (but not if it's the refresh endpoint itself)
      if (error?.response?.status === 401 && refreshToken && !originalRequest._retry && !isRefreshEndpoint) {
        originalRequest._retry = true;

        // If already refreshing, wait for that promise instead of starting a new one
        if (isRefreshing()) {
          try {
            // We just need to wait for the refresh to finish, executeRefresh will handle joining
            await executeRefresh(() => Promise.resolve());
            // Retry original request with new token and exponential backoff
            const newToken = getAccessToken(); // Checks both storage types
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            pushRefreshedTokenToSocket(newToken);
            return retryRequestWithBackoff(() => apiClient(originalRequest), 0, 1);
          } catch (e) {
            // Refresh failed, handle error
            // Always use performLogout to ensure proper cleanup for both auth methods
            await performLogout('jwt:refresh-failed-join-queue', { url: requestUrl });
            throw e;
          }
        }

        // Start new refresh
        const performRefresh = async () => {
          const response = await apiClient.post('/auth/refresh-token', {
            refreshToken: refreshToken
          });

          const tokens = extractTokensFromResponse(response);

          // Store tokens in the same storage type as the original tokens
          if (tokens.accessToken) {
            if (isUsingSessionStorage) {
              setAccessTokenToSessionStorage(tokens.accessToken);
            } else {
              setAccessTokenToLocalStorage(tokens.accessToken);
            }
          }
          if (tokens.refreshToken) {
            if (isUsingSessionStorage) {
              setRefreshTokenToSessionStorage(tokens.refreshToken);
            } else {
              setRefreshTokenToLocalStorage(tokens.refreshToken);
            }
          }
          if (tokens.expiresIn) {
            setTokenExpiration(tokens.expiresIn);
          }

          return tokens;
        };

        try {
          const tokens = await executeRefresh(performRefresh);

          // Update authorization header
          if (tokens?.accessToken) {
            originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
          }

          pushRefreshedTokenToSocket(tokens?.accessToken);

          // Refresh user data after successful token refresh
          try {
            const store = getUserStoreSafe();
            await store?.refreshUserData();
          } catch (userError) {
            // Don't fail the request if user data refresh fails
            console.debug('[API] Failed to refresh user data after token refresh:', userError);
          }

          // Retry original request with exponential backoff (max 1 retry since token was just refreshed)
          return retryRequestWithBackoff(() => apiClient(originalRequest), 0, 1);
        } catch (refreshError) {
          // Refresh failed - set security message if backend indicates reuse or re-login required (per backend guide)
          const code = refreshError?.response?.data?.code;
          const requiresReLogin = refreshError?.response?.data?.requiresReLogin;
          if (code === 'TOKEN_REUSE_DETECTED' || requiresReLogin === true) {
            const authStoreForMessage = getAuthStoreSafe();
            authStoreForMessage?.setLogoutSecurityMessage(
              t('auth.sessionInvalidatedSecurity') || 'Your session was invalidated for security reasons. Please sign in again.'
            );
          }
          await performLogout('jwt:refresh-failed-new', { url: requestUrl, code });
          throw refreshError;
        }
      }
      // No refresh token available
      if (error?.response?.status === 401 && !refreshToken) {
        // Suppress expected errors after logout or on auth pages
        const authStore = getAuthStoreSafe();
        if (authStore?.shouldSuppressError?.(error, router)) {
          guestDebug('api:401 suppressed (no logout)', { url: requestUrl });
          clearCredentials();
          throw error; // Still throw to prevent further processing, but silently
        }

        // Always use performLogout to ensure proper cleanup for both auth methods
        await performLogout('jwt:401-no-refresh-token', { url: requestUrl });
      }

      // Handle 503 Service Unavailable with retry
      else if (error?.response?.status === 503) {
        if (!originalRequest._retry503) {
          originalRequest._retry503 = true;
          return retryRequestWithBackoff(() => apiClient(originalRequest));
        }
        applyUiSafeErrorMessage(error);
      }
      // Handle 500 Internal Server Error with retry
      else if (error?.response?.status === 500) {
        if (!originalRequest._retry500) {
          originalRequest._retry500 = true;
          return retryRequestWithBackoff(() => apiClient(originalRequest));
        }
        applyUiSafeErrorMessage(error);
      } else if (handleOAuthOnboardingRequired(error, requestUrl)) {
        throw error;
      } else if (
        error?.response?.status === 400 ||
        error?.response?.status === 403 ||
        error?.response?.status === 404 ||
        error?.response?.status === 409 ||
        error?.response?.status === 429
      ) {
        applyUiSafeErrorMessage(error);
      }
      // Handle other critical errors
      else if (isCriticalError) {
        // Suppress expected errors after logout or on auth pages
        const authStore = getAuthStoreSafe();
        if (authStore?.shouldSuppressError?.(error, router)) {
          clearCredentials();
          throw error;
        }
        await performLogout('jwt:critical-error-other', { url: requestUrl, status: error?.response?.status });
      }

      throw error;
    }
  );
}
else {
  // Cookie-based authentication

  // Request interceptor for cookie-based auth - prevent calls after logout
  apiClient.interceptors.request.use(
    (config) => {
      // ⚠️ CRITICAL: Prevent API calls if user has logged out
      const authStore = getAuthStoreSafe();

      // ✅ FIX: Whitelist auth endpoints to allow login even if logged out
      const isAuthEndpoint = config.url && (
        config.url.includes('/auth/login') ||
        config.url.includes('/auth/signin') ||
        config.url.includes('/auth/sign-in') ||
        config.url.includes('/auth/sign-up') ||
        config.url.includes('/auth/cookie/login') ||
        config.url.includes('/auth/cookie/signin') ||
        config.url.includes('/auth/cookie/signup') ||
        config.url.includes('/auth/cookie/refresh') ||
        config.url.includes('/auth/cookie/logout') ||
        config.url.includes('/auth/refresh-token') ||
        config.url.includes('/auth/logout') ||
        config.url.includes('/auth/oauth') ||
        config.url.includes('/auth/onboarding') ||
        config.url.includes('/auth/login/methods') ||
        config.url.includes('/auth/recover') ||
        config.url.includes('/auth/reset') ||
        config.url.includes('/auth/verify')
      );

      if (authStore?.shouldPreventApiCall && !isAuthEndpoint) {
        console.debug('[API] Preventing API call after logout:', config.url);
        return Promise.reject(new Error('User has logged out'));
      }

      if (isPublicGuestApiPath(config.url || '')) {
        guestDebug('api:cookie-request skip credentials (public guest path)', { url: config.url });
        config.withCredentials = false;
        delete config.headers.Authorization;
        return config;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  apiClient.interceptors.response.use(
    (response) => response,

    async (error) => {
      const originalRequest = error.config;
      const requestUrl = originalRequest?.url || '';
      if (isPublicGuestApiPath(requestUrl)) {
        guestDebug('api:response error on public guest path (no logout)', {
          url: requestUrl,
          status: error?.response?.status,
        });
        return Promise.reject(error);
      }

      // Exclude refresh endpoint from retry logic
      const isRefreshEndpoint = originalRequest.url?.includes('/auth/cookie/refresh');

      // Check if refresh endpoint failed with "Refresh token is required" - this means cookies are missing
      // This should immediately logout to prevent infinite loops
      const isRefreshTokenRequired = error?.response?.data?.code === 'AUTH_REFRESH_TOKEN_REQUIRED' ||
        (isRefreshEndpoint && error?.response?.status === 401);

      if (isRefreshTokenRequired) {
        await performLogout('cookie:refresh-token-required', { url: requestUrl });
        throw error;
      }

      const isCriticalError = isSessionExpiredOrUserNotFound(error);
      const errorMessage = (error?.response?.data?.message || '').toLowerCase();
      const isDeactivatedAccount = errorMessage.includes('deactivated');

      // Only logout immediately for critical errors when we cannot try refresh (per backend guide: on 401 try refresh first)
      const canTryCookieRefresh = error?.response?.status === 401 && !isRefreshEndpoint;
      if (isCriticalError && !isDeactivatedAccount && !canTryCookieRefresh) {
        await performLogout('cookie:critical-error-no-refresh', { url: requestUrl, status: error?.response?.status });
        throw error;
      }

      // Try to refresh cookies on 401 error (but not if it's the refresh endpoint itself)
      if (error?.response?.status === 401 && !originalRequest._retry && !isRefreshEndpoint) {
        originalRequest._retry = true;

        // If already refreshing, wait for that promise
        if (isCookieRefreshing()) {
          try {
            // Join the existing refresh promise
            await executeCookieRefresh(() => Promise.resolve());
            pushRefreshedTokenToSocket(getAccessTokenToCookies());
            // Retry original request with exponential backoff (max 1 retry)
            return retryRequestWithBackoff(() => apiClient(originalRequest), 0, 1);
          } catch (e) {
            await performLogout('cookie:refresh-failed-join-queue', { url: requestUrl });
            throw e;
          }
        }

        // Start new refresh
        const performCookieRefresh = async () => {
          const refreshResponse = await refreshCookies();
          return refreshResponse;
        };

        try {
          const refreshResponse = await executeCookieRefresh(performCookieRefresh);

          if (refreshResponse.success) {
            pushRefreshedTokenToSocket(getAccessTokenToCookies());

            // Refresh user data after successful token refresh
            try {
              const store = getUserStoreSafe();
              await store?.refreshUserData();
            } catch (userError) {
              // Don't fail the request if user data refresh fails
              console.debug('[API] Failed to refresh user data after token refresh:', userError);
            }

            // Retry original request with exponential backoff (max 1 retry)
            // Note: Cookies are automatically sent via withCredentials: true
            // No need to manually set tokens since they're httpOnly cookies set by backend
            return retryRequestWithBackoff(() => apiClient(originalRequest), 0, 1);
          } else {
            await performLogout('cookie:refresh-response-failed', { url: requestUrl });
            throw error;
          }
        } catch (refreshError) {
          // Refresh failed with exception, always clear cookies
          // Log the error for debugging
          console.error('[API] Cookie refresh failed:', {
            error: refreshError?.message,
            code: refreshError?.response?.data?.code,
            status: refreshError?.response?.status,
            message: refreshError?.response?.data?.message,
          });

          // Set security message if backend indicates reuse or re-login required (per backend guide)
          const code = refreshError?.response?.data?.code;
          const requiresReLogin = refreshError?.response?.data?.requiresReLogin;
          if (code === 'TOKEN_REUSE_DETECTED' || requiresReLogin === true) {
            const authStoreForMessage = getAuthStoreSafe();
            authStoreForMessage?.setLogoutSecurityMessage(
              t('auth.sessionInvalidatedSecurity') || 'Your session was invalidated for security reasons. Please sign in again.'
            );
          }

          await performLogout('cookie:refresh-failed-new', { url: requestUrl, code });
          throw refreshError;
        }
      }
      // Already retried or no refresh possible
      // Handle 503 Service Unavailable with retry
      if (error?.response?.status === 503) {
        if (!originalRequest._retry503) {
          originalRequest._retry503 = true;
          return retryRequestWithBackoff(() => apiClient(originalRequest));
        }
        applyUiSafeErrorMessage(error);
      }
      // Handle 500 Internal Server Error with retry
      else if (error?.response?.status === 500) {
        if (!originalRequest._retry500) {
          originalRequest._retry500 = true;
          return retryRequestWithBackoff(() => apiClient(originalRequest));
        }
        applyUiSafeErrorMessage(error);
      } else if (handleOAuthOnboardingRequired(error, requestUrl)) {
        throw error;
      } else if (
        error?.response?.status === 400 ||
        error?.response?.status === 403 ||
        error?.response?.status === 404 ||
        error?.response?.status === 409 ||
        error?.response?.status === 429
      ) {
        applyUiSafeErrorMessage(error);
      } else if (error?.response?.status === 401 || isCriticalError) {
        const requestUrl = originalRequest?.url || '';
        if (isPublicGuestApiPath(requestUrl)) {
          return Promise.reject(error);
        }

        // Suppress expected errors after logout or on auth pages
        const authStore = getAuthStoreSafe();
        if (authStore?.shouldSuppressError?.(error, router)) {
          clearCredentialsFromCookies();
          throw error; // Still throw to prevent further processing, but silently
        }

        await performLogout('cookie:401-or-critical', { url: requestUrl, status: error?.response?.status });
      }

      throw error;
    }
  );
}

export { apiClient }
